/**
 * useCheckSlug — debounced slug-availability probe.
 *
 * Calls GET /api/v1/auth/check-slug with a 350 ms debounce so the
 * signup form can give live feedback as the user types without
 * spamming the backend. The endpoint always returns 200 with a
 * structured payload (valid / available / suggestions / reason),
 * so we surface that shape verbatim.
 *
 * Returns:
 *   - state: 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'
 *   - data: the latest CheckSlugResponse or null
 *   - error: thrown error if the network request itself failed
 */
import { useEffect, useRef, useState } from 'react'

import { api } from '@/lib/api'


export interface CheckSlugResponse {
  slug: string
  valid: boolean
  available: boolean
  suggestions: string[]
  reason: string | null
}


export type CheckSlugState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'unavailable'
  | 'invalid'


export interface UseCheckSlugResult {
  state: CheckSlugState
  data: CheckSlugResponse | null
  error: unknown
}


export function useCheckSlug(
  slug: string,
  { debounceMs = 350, minLength = 3 }: {
    debounceMs?: number
    minLength?: number
  } = {},
): UseCheckSlugResult {
  const [data, setData] = useState<CheckSlugResponse | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [state, setState] = useState<CheckSlugState>('idle')
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    setError(null)

    const trimmed = slug.trim()
    if (trimmed.length < minLength) {
      setState('idle')
      setData(null)
      return
    }

    setState('checking')
    const t = setTimeout(async () => {
      // Cancel any in-flight request from a prior keystroke.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const { data: payload } = await api.get<CheckSlugResponse>(
          '/auth/check-slug',
          { params: { slug: trimmed }, signal: controller.signal },
        )
        // If the caller has typed since this request fired, ignore
        // the response — the cleanup function will run on the next
        // effect cycle. Comparing against ``trimmed`` is the cheap
        // version of that check.
        setData(payload)
        if (!payload.valid) {
          setState('invalid')
        } else if (payload.available) {
          setState('available')
        } else {
          setState('unavailable')
        }
      } catch (e: unknown) {
        // axios cancellations show up as ``ERR_CANCELED`` errors —
        // ignore them silently.
        const code = (e as { code?: string } | null)?.code
        const name = (e as { name?: string } | null)?.name
        if (code === 'ERR_CANCELED' || name === 'CanceledError') {
          return
        }
        setError(e)
        setState('idle')
        setData(null)
      }
    }, debounceMs)

    return () => {
      clearTimeout(t)
      abortRef.current?.abort()
    }
  }, [slug, debounceMs, minLength])

  return { state, data, error }
}
